'use client';

import {
  createContext,
  useContext,
  useState,
  useMemo
} from 'react'
import { useParams, useSearchParams } from 'next/navigation';

// Apis
import { useQueryPropertyCategories } from "@src/apis/shared/get-property-categories"
import { useGetListPropertiesCompany } from '@src/apis/list-listing/query';

const ListListingCtx = createContext();

const LIMIT = 15;

const initForm = {
  "price[min]": 0,
  "price[max]": 0,
  'bedroom': null,
  'additional_bedroom': null,
  'bathroom': null,
  'additional_bathroom': null,
  "surface_area[min]": 0,
  "surface_area[max]": 0,
  "building_area[min]": 0,
  "building_area[max]": 0,
  "_order": null,
  "_limit": LIMIT,
  "page": 1,
}
export const ListListingProvider = ({ children }) => {
  const { companySlug } = useParams()
  const searchParams = useSearchParams()
  const propertyType = searchParams.get("property_type_id")
  const cityId = searchParams.get("city_id")
  const areaIds = searchParams.get("area_ids")
  const keyword = searchParams.get('q');

  const [propertyCategory, setPropertyCategory] = useState(null);
  const [filter, setFilter] = useState({ ...initForm });
  const [searchDisplay, setSearchDisplay] = useState("")

  const [params, setParams] = useState({
    property_category_id: null,
    property_type_id: propertyType,
    area_ids: areaIds,
    city_id: cityId,
    keyword: keyword,
    ...filter
  })

  const { data: listPropertyCategories } = useQueryPropertyCategories();

  const newParams = useMemo(() => {
    return Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== 0)
    );
  }, [params]);

  const {
    data: listListing,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage
  } = useGetListPropertiesCompany({ params: newParams, slug: companySlug })

  const handleNextPage = () => {

    let newParams = {
      ...params,
      page: params.page + 1
    };
    setParams(newParams);
  }

  const handleApply = (type, value, callback) => {
    let newParams = {
      ...params,
      page: 1
    };

    switch (type) {
      case "property-type": {
        newParams.property_type_id = value;
        break;
      }
      case "property-category": {
        newParams.property_category_id = !!value?.id ? `${value.id}` : null;
        break;
      }
      case "filter": {
        newParams = {
          ...newParams,
          ...value
        }
        break;
      }
      case "search": {
        if (value?.slug === "Area") {
          newParams.area_ids = !!value?.id ? `${value.id}` : null;
          newParams.city_id = null;
        } else if (value?.slug === "City") {
          newParams.city_id = !!value?.id ? `${value.id}` : null;
          newParams.area_ids = null;
        } else if (value?.slug === "Keyword") {
          newParams.keyword = value?.name ?? "";
          newParams.city_id = null;
          newParams.area_ids = null;
        } else {
          newParams.keyword = null;
          newParams.city_id = null;
          newParams.area_ids = null;
        }
        break;
      }
      case "order":
      default: {
        newParams._order = value;
        break;
      }
    }

    setParams(newParams);
    typeof callback === "function" && callback();
  }

  return (
    <ListListingCtx.Provider value={{
      isLoading,
      hasNextPage,
      handleNextPage,
      propertyCategory,
      setPropertyCategory,
      listPropertyCategories,
      initForm,
      filter,
      setFilter,
      handleApply,
      params,
      fetchNextPage,
      isFetchingNextPage,
      listListing,
      searchDisplay, 
      setSearchDisplay,
      setParams
    }}>
      {children}
    </ListListingCtx.Provider>
  )
}


const useListListingCtx = () => {
  const ctx = useContext(ListListingCtx);

  if (!ctx) {
    throw new Error("useListListingCtx must be used within the ListListingProvider");
  }

  return ctx;
};


export default useListListingCtx